import {
    DataTable,
    TikTokTableCol,
    TikTokTableHeadColFilter,
} from 'components/DataVis/DataTable';
import { AiGeneratedLabel } from '@/ui/design-system/components';
import Badges from '../Post/Badges';
import { FiLink } from 'react-icons/fi';
import Stack from '../Stack';
import TrackedExtLinkButton from '../Button/TrackedExtLinkButton';
import { createDefaultColumns } from '../DataVis/DataTable/utils';
import { getWeekRange } from './utils';
import styled from 'styled-components';

const TopPostSummaryColWrapper = styled.span`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SummaryText = styled.span`
    font-size: 0.75rem;
    white-space: normal;
    color: ${({ theme }) => theme.gray_2};
    line-height: 1.2;
    text-transform: none;
    display: flex;
`;

const NoSentimentSummary = () => (
    <span css="align-self: center">No sentiment analysis available.</span>
);

const TopPostSummaryCol = ({ row }) => (
    <TopPostSummaryColWrapper>
        <Badges
            css={`
                font-size: 0.625rem;
            `}
            post={{
                week: row.week,
                year: row.year,
                isNew: row.isNew,
                isPaid: row.isPaid,
                isShop: row.isShop,
                paidTypesMessage: row.paidTypesMessage,
            }}
        />
        <Stack
            $space=".25rem"
            $direction="row"
            css={`
                display: flex;
            `}
        >
            <span>
                <TrackedExtLinkButton
                    href={row.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    IconLeft={FiLink}
                    variant="ghost"
                    size="sm"
                    className="text-[0.875rem]"
                />
            </span>
            <SummaryText>
                {row.sentiment_summary || <NoSentimentSummary />}
            </SummaryText>
        </Stack>
    </TopPostSummaryColWrapper>
);

const noDataRow = [{ fetching: true, fetchingData: true, id: 'fetching' }];

function TopPostsTable({
    topPostsQuery,
    sortOption,
    setSortOption,
    visibleOrderedMetrics,
    week,
    year,
    name,
    onSearchInputChange,
    dataKey,
    noDataMessage,
}) {
    const visibleColumnsConfig = createDefaultColumns(
        ['name', ...visibleOrderedMetrics],
        { showHealthStatLabels: false },
    );
    visibleColumnsConfig[0].$width = '60%';
    visibleColumnsConfig[0].formatCell = ({ row }) => (
        <TopPostSummaryCol row={row} />
    );
    const latestWeekConfig = visibleColumnsConfig.find(
        ({ key }) => key === 'last_week_views',
    );
    // Absent when the user hides it from the Columns menu.
    if (week && year && latestWeekConfig) {
        const label = `Last Week (${getWeekRange({ week, year })})`;
        latestWeekConfig.label = `Views`;
        latestWeekConfig.tooltip.title = label;
    }

    const isPending = topPostsQuery.isLoading || topPostsQuery.isIdle;
    const hasAiSummary =
        !isPending &&
        (topPostsQuery.data || []).some((row) => row.sentiment_summary?.trim());

    // One table-level notice rather than a label in every row's summary.
    return (
        <div className="flex flex-col gap-2">
            {hasAiSummary && <AiGeneratedLabel />}
            <DataTable
                data={isPending ? noDataRow : topPostsQuery.data}
                dataKey={dataKey}
                columns={visibleColumnsConfig}
                sortOption={sortOption}
                setSortOption={setSortOption}
                noDataMessage={noDataMessage}
                onSearchInputChange={onSearchInputChange}
                onRowClick={false}
                rowHeight={125}
                week={week}
                year={year}
                name={name}
                TableCol={TikTokTableCol}
                TableHeadColFilter={TikTokTableHeadColFilter}
            />
        </div>
    );
}

export default TopPostsTable;
